import { NavigatorData, NavigatorEntry, ProductId } from './NavigatorData';

export enum DownloadProductTypes {
  xmlCompareJava = 'XML Compare Java',
  xmlCompareRest = 'XML Compare REST',
  xmlMergeJava = 'XML Merge Java',
  xmlMergeRest = 'XML Merge REST',
  ditaCompareJava = 'DITA Compare Java',
  ditaCompareRest = 'DITA Compare REST',
  ditaMergeJava = 'DITA Merge Java',
  ditaMergeRest = 'DITA Merge REST',
  xmlDataCompareJava = 'XML Data Compare',
  docBookCompareJava = 'DocBook Compare Java',
  docbookCompareRest = 'DocBook Compare REST',
  deltaJSONJava = 'DeltaJSON',
  conversionQAJava = 'ConversionQA Java',
  conversionQARest = 'ConversionQA REST',
  linuxServer = 'Linux',
  windowsServer = 'Windows',
  macIntelServer = 'Mac (Intel)',
  macARMServer = 'Mac (ARM)',
  htmlCompareRest = 'HTML Compare REST',
  deltaNovaJava = 'DeltaNova Java',
  deltaNovaRest = 'DeltaNova REST'
}

export enum LicenseServerTypes {
  linux = "linux_x64",
  windows = "win_x64",
  macIntel = "macos",
  macARM = "arm" 
}

export interface ProductsDataSet {
  expiryTime?: string;
  products: ProductsData[];
  license_servers: DownloadInfo[];
}

export interface ProductsData {
  product: string;
  legacy: boolean;
  downloads: DownloadInfo[];
}

export interface DownloadInfo {
  filename: string;
  release_date: string;
  download_url: string;
  current_release?: boolean;
  version: string;
  expiry_time?: string;
}

export enum Platform {
  JAVA = 'JAVA',
  DOTNET = '.NET'
}

export const initialiseDownload: DownloadInfo = {
  filename: "",
  current_release: false,
  download_url: "",
  release_date: "",
  version: "",
  expiry_time: ""

}

export function getCurrentReleaseProductData(productName: string, platform: string, jsonObject: ProductsDataSet) {
  let productData: DownloadInfo = { filename: '', current_release: false, download_url: '', release_date: '', version: '' };
  let currentDownload: DownloadInfo | undefined;
  if (jsonObject !== undefined) {
    const products = jsonObject.products;
    const currentProduct = products.find(prod => prod.product.includes(productName));
    if (platform === Platform.JAVA) {
      currentDownload = currentProduct?.downloads.find(download => download.current_release === true && (download.filename.includes("_j.zip") || download.filename.includes("DeltaJSON") || download.filename.includes("Data-Compare")
    || download.filename.includes("DeltaNova") || download.filename.includes("DeltaInsights") || download.filename.includes("DeltaOxygenPlugin")));
    } else if (platform === Platform.DOTNET) {
      currentDownload = currentProduct?.downloads.find(download => download.current_release === true && download.filename.includes("_n.zip"));
    }
    if (currentDownload !== undefined) {
      productData = {
        filename: currentDownload?.filename!,
        current_release: currentDownload?.current_release,
        download_url: currentDownload?.download_url!,
        release_date: currentDownload?.release_date!,
        version: currentDownload?.version!
      };
    }
  }
  return productData;
}

export function getCurrentReleaseServerData(os: string, jsonObject: ProductsDataSet) {
  let licenseServerData: DownloadInfo = { filename: '', current_release: false, download_url: '', release_date: '', version: '' };
  if (jsonObject !== undefined) {
    const servers = jsonObject.license_servers;
    servers.forEach((server) => {
      if (server.current_release === true) {
        if ((server.filename.includes(LicenseServerTypes.linux) && os === DownloadProductTypes.linuxServer) ||
          (server.filename.includes(LicenseServerTypes.windows) && os === DownloadProductTypes.windowsServer) ||
          (server.filename.includes(LicenseServerTypes.macIntel) && os === DownloadProductTypes.macIntelServer) ||
          (server.filename.includes(LicenseServerTypes.macARM) && os === DownloadProductTypes.macARMServer)
        ) {
          licenseServerData = {
            filename: server.filename,
            current_release: server.current_release,
            download_url: server.download_url,
            release_date: server.release_date,
            version: server.version
          };
        }
      }
    });
  }
  return licenseServerData;
}

export function getAllProductData(jsonObject: ProductsDataSet, productName?: string, os?: string) {
  let productData: DownloadInfo[] = [];
  
  if (jsonObject !== undefined) {
    if (productName) {
      const products = jsonObject.products;
      const productJavaOrRest = products.filter(prod => prod.product.includes(productName));
      let currentProduct: ProductsData;
      if (productJavaOrRest.length > 1) {
        currentProduct = productJavaOrRest.find(prod => prod.product.includes("Java"))!;
      } else {
        currentProduct = productJavaOrRest.pop()!;
      }
      const downloads = currentProduct?.downloads;
      downloads?.forEach((download) => {
        productData.push({
          filename: download.filename,
          current_release: download.current_release,
          download_url: download.download_url,
          release_date: download.release_date,
          version: download.version,
          expiry_time: download.expiry_time
        })
      });
    } else {
      const servers = jsonObject.license_servers;
      servers.forEach((server) => {
        if ((server?.filename.includes(LicenseServerTypes.linux) && os === DownloadProductTypes.linuxServer) ||
          (server?.filename.includes(LicenseServerTypes.windows) && os === DownloadProductTypes.windowsServer) ||
          (server?.filename.includes(LicenseServerTypes.macIntel) && os === DownloadProductTypes.macIntelServer) ||
          (server?.filename.includes(LicenseServerTypes.macARM) && os === DownloadProductTypes.macARMServer)
        ) {
          productData.push({
            filename: server.filename,
            current_release: server.current_release,
            download_url: server.download_url,
            release_date: server.release_date,
            version: server.version,
          });
        }
      });
    }
  }
  return productData;
}


export function findProductData(prodName: string, downloadProductData: ProductsDataSet): [DownloadInfo, DownloadInfo] {

  let productDownloadJavaOrRest: DownloadInfo = { "filename": '', "current_release": false, "download_url": '', "release_date": '', "version": '', "expiry_time": '' };
  let productDownloadDotNet: DownloadInfo = { "filename": '', "current_release": false, "download_url": '', "release_date": '', "version": '', "expiry_time": '' };

  productDownloadJavaOrRest = getCurrentReleaseProductData(prodName, Platform.JAVA, downloadProductData);
  const productsWithDotNet = [ProductId.xmlCompare, ProductId.ditaCompare, ProductId.docbookCompare];
  if (productsWithDotNet.some(prod => prod === prodName)) {
    productDownloadDotNet = getCurrentReleaseProductData(prodName, Platform.DOTNET, downloadProductData);
  }
  return [productDownloadJavaOrRest, productDownloadDotNet];
}


export function findServerData(serverId: string, downloadProductData: ProductsDataSet): [DownloadInfo, string, string] {
  let serverDownload: DownloadInfo = { filename: '', current_release: false, download_url: '', release_date: '', version: '' };
  let serverPlatform: string = "";
  let serverNameOS: string = "";

  switch(serverId){
    case ProductId.linuxLicenseServer:
        serverDownload = getCurrentReleaseServerData(DownloadProductTypes.linuxServer, downloadProductData)
        serverPlatform = 'Linux (x64)'
        serverNameOS = DownloadProductTypes.linuxServer
        break;
    case ProductId.windowsLicenseServer:
        serverDownload = getCurrentReleaseServerData(DownloadProductTypes.windowsServer, downloadProductData)
        serverPlatform = 'Windows (x64)'
        serverNameOS = DownloadProductTypes.windowsServer
        break;
    case ProductId.macIntelLicenseServer:
        serverDownload = getCurrentReleaseServerData(DownloadProductTypes.macIntelServer, downloadProductData)
        serverPlatform = 'Mac (Intel)'
        serverNameOS = DownloadProductTypes.macIntelServer
        break;
    case ProductId.macARMLicenseServer:
        serverDownload = getCurrentReleaseServerData(DownloadProductTypes.macARMServer, downloadProductData)
        serverPlatform = 'Mac (ARM)'
        serverNameOS = DownloadProductTypes.macARMServer
}
  return [serverDownload, serverPlatform, serverNameOS];
}



export function getProductTitle(productType: string) {
  return productType.endsWith(' Java') || productType.endsWith(' REST') ?
    productType.substring(0, productType.length - 5) :
    productType;
}

export function getServerTitle(serverType: string) {
  if (serverType.includes(LicenseServerTypes.linux)) {
    return ProductId.linuxLicenseServer
  } else if (serverType.includes(LicenseServerTypes.windows)) {
    return ProductId.windowsLicenseServer
  } else if (serverType.includes(LicenseServerTypes.macIntel) && !serverType.includes(LicenseServerTypes.macARM)) {
    return ProductId.macIntelLicenseServer
  } else if (serverType.includes(LicenseServerTypes.macARM)) {
    return ProductId.macARMLicenseServer
  }
}


export function getDetails(dataSet: ProductsDataSet | undefined, products: boolean, legacy?: boolean) {
  let sections;
  if (dataSet) {
    if (products) {
      if (dataSet.products) {
        const filteredProductsDataset = dataSet.products.filter((section) => section.legacy === legacy);  
        sections = filteredProductsDataset.map((productData) => {
          const title = getProductTitle(productData.product);
          const navSection = NavigatorData.DownloadSections.find((data) => data.id === title);
          return navSection!;
        })
      }
    } else {
      if (dataSet.license_servers) {
        sections = dataSet.license_servers.map((serverData) => {
          const title = getServerTitle(serverData.filename)
          const navSection = NavigatorData.DownloadSections.find((data) => data.id === title)
          return navSection!
        })
      }
    }
    // filter sections as duplicate entries exist when user has licenses for Java and REST products
    const filteredSections = sections?.filter(
      function (section, index, self) {
        return index === self.indexOf(section);
      }
    );

    // sort sections so that DeltaNova and DeltaInsights always appear at the top of the list as compared to othe solutions
    filteredSections?.sort((a,b) => {
      const idA = a.id.toUpperCase();
      const idB = b.id.toUpperCase();
      if (idA > idB) {return -1}
      if (idA < idB) {return 1}
      return 0;
    });

    return filteredSections;
  } else {
    return [];
  }
}